/*
 * TM24 Search Core 1.0.3
 * One deterministic, side-effect-free search kernel for finder and collection
 * contexts. No DOM, network, cart, price, vendor or tracking responsibilities.
 */
(function tm24SearchCoreFactory(global, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (global) global.TM24SearchCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTM24SearchCore() {
  'use strict';

  // VERSION is part of the controller's index/cache key. Bumping it prevents
  // products normalized with the previous umlaut rules from being reused.
  const VERSION = '1.0.5';
  const MAX_QUERY_LENGTH = 120;
  const MAX_SUGGESTIONS = 6;
  const documentCache = new WeakMap();

  const STOPWORDS = new Set([
    'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem', 'einer',
    'und', 'oder', 'mit', 'ohne', 'von', 'vom', 'fur', 'fuer', 'am', 'an', 'im', 'in', 'auf',
    'zu', 'zum', 'zur', 'bitte', 'suche', 'suchen', 'brauche', 'benotige', 'benoetige', 'kaufen',
    'bestellen', 'defekt', 'kaputt', 'problem', 'probleme', 'ersatz', 'teil',
    'de', 'het', 'een', 'en', 'of', 'met', 'voor', 'zoek', 'zoeken',
    'le', 'la', 'les', 'un', 'une', 'et', 'ou', 'avec', 'pour', 'recherche',
  ]);

  const DEFAULT_MANUAL = Object.freeze({
    version: 'tm24-vocabulary-2026-08-12',
    corrections: {
      laufrole: 'Laufrolle',
      'lauf rolle': 'Laufrolle',
      'lauf-rolle': 'Laufrolle',
      hoermann: 'Hörmann',
      hormann: 'Hörmann',
      highline: 'High-Line',
      'high line': 'High-Line',
      selectline: 'Select-Line',
      'select line': 'Select-Line',
      proline: 'Pro-Line',
      'pro line': 'Pro-Line',
    },
    aliases: [
      { term: 'Hörmann', aliases: ['Hoermann', 'Hormann'] },
      { term: 'High-Line', aliases: ['High Line', 'Highline'] },
      { term: 'Select-Line', aliases: ['Select Line', 'Selectline'] },
      { term: 'Pro-Line', aliases: ['Pro Line', 'Proline'] },
      { term: 'Laufrolle', aliases: ['Lauf Rolle', 'lauf-rolle', 'laufrole'] },
    ],
    diagnoses: [],
  });

  function scalar(value) {
    if (value == null) return '';
    if (Array.isArray(value)) return value.map(scalar).filter(Boolean).join(' ');
    if (typeof value === 'object') {
      if ('value' in value) return scalar(value.value);
      return '';
    }
    return String(value);
  }

  function list(value) {
    if (value == null) return [];
    if (Array.isArray(value)) return value.flatMap(list).filter(Boolean);
    if (typeof value === 'object' && 'value' in value) return list(value.value);
    const string = String(value).trim();
    return string ? [string] : [];
  }

  function normalizeText(input, maximumLength) {
    return scalar(input)
      .slice(0, Math.max(MAX_QUERY_LENGTH * 4, maximumLength * 4))
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/[<>]/g, ' ')
      // Fold German characters in one safe direction. Umlauts become their
      // established ASCII spelling, while existing `ae`/`oe`/`ue` sequences
      // remain untouched. This makes Hörmann/Hoermann equivalent without a
      // risky global ASCII-to-umlaut rewrite inside names or identifiers.
      .replace(/Ä/g, 'Ae')
      .replace(/Ö/g, 'Oe')
      .replace(/Ü/g, 'Ue')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/ẞ/g, 'SS')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('und')
      .replace(/[’'`´]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, maximumLength);
  }

  function normalize(input) {
    return normalizeText(input, MAX_QUERY_LENGTH);
  }

  function compact(input) {
    return normalize(input).replace(/[^a-z0-9]/g, '');
  }

  function titleKey(input) {
    return compact(input);
  }

  function numericId(value) {
    return scalar(value).split('/').pop();
  }

  function editDistance(leftInput, rightInput, maximum) {
    const left = String(leftInput || '');
    const right = String(rightInput || '');
    if (left === right) return 0;
    if (!left || !right) return Math.max(left.length, right.length);
    if (Math.abs(left.length - right.length) > maximum) return maximum + 1;
    let previousPrevious = null;
    let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let row = 1; row <= left.length; row += 1) {
      const current = [row];
      let rowMinimum = row;
      for (let column = 1; column <= right.length; column += 1) {
        const substitution = previous[column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1);
        let value = Math.min(previous[column] + 1, current[column - 1] + 1, substitution);
        if (
          row > 1 && column > 1
          && previousPrevious
          && left[row - 1] === right[column - 2]
          && left[row - 2] === right[column - 1]
        ) value = Math.min(value, previousPrevious[column - 2] + 1);
        current[column] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }
      if (rowMinimum > maximum) return maximum + 1;
      previousPrevious = previous;
      previous = current;
    }
    return previous[right.length];
  }

  function fuzzyLimit(token) {
    const length = String(token || '').length;
    if (length <= 4) return 0;
    if (length <= 7) return 1;
    return 2;
  }

  function mergeManual(manual) {
    const supplied = manual && typeof manual === 'object' ? manual : {};
    const locale = String(supplied.locale || '').toLowerCase();
    const useGermanDefaults = !locale || locale === 'de' || locale.startsWith('de-');
    const defaultAliases = useGermanDefaults
      ? DEFAULT_MANUAL.aliases
      : DEFAULT_MANUAL.aliases.filter((entry) => entry.term !== 'Laufrolle');
    return {
      locale,
      version: supplied.version || DEFAULT_MANUAL.version,
      corrections: { ...(useGermanDefaults ? DEFAULT_MANUAL.corrections : {}), ...(supplied.corrections || {}) },
      aliases: [...defaultAliases, ...(Array.isArray(supplied.aliases) ? supplied.aliases : [])],
      diagnoses: Array.isArray(supplied.diagnoses) ? supplied.diagnoses : [],
      terms: Array.isArray(supplied.terms) ? supplied.terms : [],
    };
  }

  function addVocabularyEntry(entries, label, kind, frequency) {
    const cleanLabel = scalar(label).trim().replace(/\s+/g, ' ');
    const normalized = normalize(cleanLabel);
    if (!cleanLabel || !normalized || cleanLabel.length > 64) return;
    const key = compact(normalized);
    if (!key || key.length < 2) return;
    const current = entries.get(key);
    const nextFrequency = Number(frequency || 1);
    if (!current) {
      entries.set(key, { label: cleanLabel, normalized, compact: key, kind, frequency: nextFrequency });
    } else {
      current.frequency += nextFrequency;
      if (cleanLabel.length < current.label.length && current.kind !== 'manufacturer') current.label = cleanLabel;
      if (kind === 'manufacturer' || (kind === 'model' && current.kind === 'term')) current.kind = kind;
    }
  }

  function createVocabulary(products, manualInput) {
    const manual = mergeManual(manualInput);
    const entries = new Map();
    const aliases = Object.create(null);
    const corrections = Object.create(null);
    const manufacturers = Object.create(null);
    const diagnoses = [];

    Object.entries(manual.corrections).forEach(([from, to]) => {
      const fromNormalized = normalize(from);
      const toNormalized = normalize(to);
      if (fromNormalized && toNormalized) {
        corrections[fromNormalized] = { label: scalar(to), normalized: toNormalized };
        addVocabularyEntry(entries, to, 'correction', 30);
      }
    });

    manual.aliases.forEach((group) => {
      const label = scalar(group?.term).trim();
      const canonical = normalize(label);
      if (!canonical) return;
      addVocabularyEntry(entries, label, 'term', 20);
      [label, ...(Array.isArray(group.aliases) ? group.aliases : [])].forEach((alias) => {
        const normalizedAlias = normalize(alias);
        if (normalizedAlias) aliases[normalizedAlias] = canonical;
      });
    });

    manual.diagnoses.forEach((diagnosis) => {
      const phrase = scalar(diagnosis?.phrase).trim();
      const normalized = normalize(phrase);
      const terms = list(diagnosis?.terms).map((term) => scalar(term).trim()).filter(Boolean);
      if (!normalized || !terms.length) return;
      diagnoses.push({
        phrase,
        normalized,
        terms,
        disclaimer: diagnosis.disclaimer !== false,
        source: scalar(diagnosis.source || manual.version),
      });
      addVocabularyEntry(entries, phrase, 'diagnosis', 8);
    });

    list(manual.terms).forEach((term) => addVocabularyEntry(entries, term, 'term', 5));

    (Array.isArray(products) ? products : []).forEach((product) => {
      const manufacturer = scalar(product?.manufacturer).trim();
      const productType = scalar(product?.productType || product?.kind || product?.type).trim();
      const model = scalar(product?.model || product?.name || product?.bezeichnung).trim();
      const series = scalar(product?.series || product?.baureihe).trim();
      const group = scalar(product?.group || product?.ersatzteilgruppe).trim();
      if (manufacturer) {
        addVocabularyEntry(entries, manufacturer, 'manufacturer', 4);
        manufacturers[normalize(manufacturer)] = manufacturer;
      }
      if (productType) addVocabularyEntry(entries, productType, 'category', 3);
      if (model && model.length <= 40) addVocabularyEntry(entries, model, 'model', 2);
      if (series && series.length <= 40) addVocabularyEntry(entries, series, 'series', 2);
      if (group && group.length <= 48) addVocabularyEntry(entries, group, 'category', 2);
      if (manufacturer && productType && `${manufacturer} ${productType}`.length <= 48) {
        addVocabularyEntry(entries, `${manufacturer} ${productType}`, 'phrase', 1);
      }
      if (model && productType && `${model} ${productType}`.length <= 48) {
        addVocabularyEntry(entries, `${model} ${productType}`, 'phrase', 1);
      }
    });

    manual.aliases.forEach((group) => {
      const manufacturerKeys = [group?.term, ...(Array.isArray(group?.aliases) ? group.aliases : [])]
        .map(normalize)
        .filter(Boolean);
      const existing = manufacturerKeys.find((key) => manufacturers[key]);
      if (!existing) return;
      manufacturerKeys.forEach((key) => {
        aliases[key] = existing;
        manufacturers[key] = manufacturers[existing];
      });
    });

    Object.entries(aliases).forEach(([alias, canonical]) => {
      const manufacturerLabel = manufacturers[canonical];
      if (manufacturerLabel) manufacturers[alias] = manufacturerLabel;
    });

    Object.entries(manual.corrections).forEach(([from, to]) => {
      const source = normalize(from);
      const target = normalize(to);
      if (!source || !target) return;
      let canonical = aliases[target] || target;
      if (manufacturers[source]) canonical = source;
      else if (!manufacturers[canonical] && manufacturers[target]) canonical = target;
      aliases[source] = canonical;
      if (manufacturers[canonical]) manufacturers[source] = manufacturers[canonical];
      else if (manufacturers[source]) manufacturers[canonical] = manufacturers[source];
    });

    return Object.freeze({
      version: manual.version,
      locale: manual.locale,
      entries: Object.freeze([...entries.values()]),
      aliases: Object.freeze(aliases),
      corrections: Object.freeze(corrections),
      manufacturers: Object.freeze(manufacturers),
      diagnoses: Object.freeze(diagnoses),
    });
  }

  function aliasPhrase(value, vocabulary) {
    const normalized = normalize(value);
    return vocabulary?.aliases?.[normalized] || normalized;
  }

  function buildQueryPlan(query, vocabularyInput) {
    const vocabulary = vocabularyInput || createVocabulary([], DEFAULT_MANUAL);
    const raw = scalar(query).slice(0, MAX_QUERY_LENGTH);
    const normalizedInput = normalize(raw);
    const correction = vocabulary.corrections?.[normalizedInput] || null;
    const corrected = correction?.normalized || normalizedInput;
    const canonicalPhrase = aliasPhrase(corrected, vocabulary);
    const diagnosis = vocabulary.diagnoses?.find((item) => item.normalized === normalizedInput || item.normalized === corrected) || null;
    const originalTokens = canonicalPhrase.split(' ').filter(Boolean);
    const canonicalTokens = originalTokens.map((token) => vocabulary.aliases?.[token] || token);
    const joinedCanonical = vocabulary.aliases?.[canonicalTokens.join(' ')] || canonicalTokens.join(' ');
    const tokens = joinedCanonical.split(' ').filter(Boolean);
    const importantTokens = [];
    const codeLabels = new Set(['typ', 'type', 'nr', 'art', 'mm', 'cm', 'kanal']);
    const categoryWords = new Set((vocabulary.entries || [])
      .filter((entry) => entry.kind === 'category').map((entry) => entry.normalized));
    for (let index = 0; index < tokens.length; index += 1) {
      const token = tokens[index];
      if (STOPWORDS.has(token)) continue;
      // Keep a separated model such as HSE 2 together. Independent substring
      // matches previously accepted "Achse" plus any unrelated number 2.
      if (/^[a-z]{2,6}$/.test(token) && /^\d+[a-z]?$/.test(tokens[index + 1] || '')
        && !vocabulary.manufacturers?.[token] && !codeLabels.has(token) && !categoryWords.has(token)) {
        importantTokens.push(token + tokens[++index]);
      } else importantTokens.push(token);
    }
    const technicalTokens = importantTokens.filter((token) => /\d/.test(token));
    const detectedManufacturerKey = importantTokens.find((token) => vocabulary.manufacturers?.[token])
      || (vocabulary.manufacturers?.[canonicalPhrase] ? canonicalPhrase : '');
    const manufacturer = detectedManufacturerKey ? {
      key: detectedManufacturerKey,
      label: vocabulary.manufacturers[detectedManufacturerKey],
    } : null;

    return Object.freeze({
      raw,
      normalizedInput,
      normalized: joinedCanonical,
      compact: compact(joinedCanonical),
      tokens: Object.freeze(tokens),
      importantTokens: Object.freeze(importantTokens),
      technicalTokens: Object.freeze(technicalTokens),
      manufacturer,
      correction: correction ? correction.label : '',
      correctionApplied: Boolean(correction),
      diagnosis,
      vocabularyVersion: vocabulary.version,
    });
  }

  function field(value) {
    const text = list(value).join(' ');
    const normalized = normalizeText(text, 5000);
    return {
      raw: text,
      normalized,
      compact: normalized.replace(/[^a-z0-9]/g, ''),
      words: normalized ? normalized.split(' ') : [],
    };
  }

  function productDocument(product) {
    if (product && typeof product === 'object' && documentCache.has(product)) return documentCache.get(product);
    const variants = Array.isArray(product?.variants) ? product.variants : (Array.isArray(product?.variant_index) ? product.variant_index : []);
    const doc = {
      id: numericId(product?.id || product?.productId),
      handle: field(product?.handle),
      title: field(product?.title),
      manufacturer: field(product?.manufacturer),
      productType: field([product?.productType, product?.kind, product?.type, product?.nativeProductType]),
      model: field([product?.model, product?.name, product?.bezeichnung]),
      series: field([product?.series, product?.baureihe]),
      group: field([product?.group, product?.ersatzteilgruppe]),
      references: field([product?.references, product?.refs]),
      keywords: field([product?.keywords, product?.terms, product?.searchTerms, product?.altTexts, product?.alt_texts, product?.tags]),
      compatibility: field([product?.compatibility, product?.passend_fuer]),
      description: field(product?.description),
      variants: variants.map((variant) => ({
        id: numericId(variant?.id || variant?.variantId),
        title: field(variant?.title),
        sku: field(variant?.sku),
      })),
    };
    doc.identifiers = [doc.references, doc.model, doc.series, ...doc.variants.flatMap((variant) => [variant.sku])];
    if (product && typeof product === 'object') documentCache.set(product, doc);
    return doc;
  }

  function containsWord(fieldValue, token) {
    if (!fieldValue.normalized || !token) return false;
    return (` ${fieldValue.normalized} `).includes(` ${token} `);
  }

  function exactCompact(fieldValue, token) {
    const key = String(token || '').replace(/[^a-z0-9]/g, '');
    return Boolean(fieldValue.compact && key && fieldValue.compact === key);
  }

  function containsCompact(fieldValue, token) {
    const key = String(token || '').replace(/[^a-z0-9]/g, '');
    return Boolean(fieldValue.compact && key && fieldValue.compact.includes(key));
  }

  function fuzzyWordMatch(words, token, maximum, cache) {
    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      if (Math.abs(word.length - token.length) > maximum) continue;
      const key = `${token}\u0000${word}`;
      if (cache?.has(key)) {
        if (cache.get(key)) return true;
        continue;
      }
      const matched = editDistance(word, token, maximum) <= maximum;
      cache?.set(key, matched);
      if (matched) return true;
    }
    return false;
  }

  function tokenMatch(fieldValue, token, allowFuzzy, fuzzyCache) {
    if (!fieldValue.normalized || !token) return null;
    if (containsWord(fieldValue, token) || exactCompact(fieldValue, token)) return { quality: 4, kind: 'exact' };
    if (/\d/.test(token)) {
      // Technical codes may differ by separators, never by an interior
      // substring or an extra digit: HSE 2 = HSE2, HSE20 and 3040311 != 3040.
      const key = token.replace(/[^a-z0-9]/g, '');
      for (let start = 0; start < fieldValue.words.length; start += 1) {
        let joined = '';
        for (let end = start; end < Math.min(start + 5, fieldValue.words.length); end += 1) {
          joined += fieldValue.words[end];
          if (joined === key) return { quality: 3, kind: 'compact' };
          if (joined.length >= key.length || !key.startsWith(joined)) break;
        }
      }
      return null;
    }
    if (containsCompact(fieldValue, token)) return { quality: 3, kind: 'compact' };
    if (token.length >= 3) {
      const words = fieldValue.words;
      // Prefix search is intentionally one-way: an unfinished query may match
      // the start of a longer catalog token. A shorter catalog token must not
      // match a longer technical query (for example 3040 must not match
      // article number 3040311).
      if (words.some((word) => word.startsWith(token))) {
        return { quality: 2, kind: 'prefix' };
      }
      const maximum = allowFuzzy ? fuzzyLimit(token) : 0;
      if (maximum > 0 && fuzzyWordMatch(words, token, maximum, fuzzyCache)) {
        return { quality: 1, kind: 'fuzzy' };
      }
    }
    return null;
  }

  function bestTokenMatch(doc, token, allowFuzzy, fuzzyCache) {
    const weighted = [
      [doc.title, 90], [doc.productType, 85], [doc.model, 88], [doc.series, 82],
      [doc.group, 78], [doc.references, 86], [doc.keywords, 62], [doc.compatibility, 52],
      [doc.handle, 58], ...doc.variants.flatMap((variant) => [[variant.sku, 90], [variant.title, 70]]),
      [doc.description, 18],
    ];
    let best = null;
    function scan(fuzzy) {
      weighted.forEach(([candidate, weight]) => {
        const match = tokenMatch(candidate, token, fuzzy, fuzzyCache);
        if (!match || (!fuzzy && match.kind === 'fuzzy')) return;
        const score = match.quality * 100 + weight;
        if (!best || score > best.score) best = { ...match, score, fieldWeight: weight };
      });
    }
    scan(false);
    if (!best && allowFuzzy) scan(true);
    return best;
  }

  function exactIdentifier(doc, queryCompact) {
    if (!queryCompact || queryCompact.length < 2) return null;
    for (const variant of doc.variants) {
      if (variant.sku.compact && variant.sku.compact === queryCompact) {
        return { matchedVariantId: variant.id, reason: 'exact_variant_sku', score: 30000 };
      }
    }
    if (doc.references.compact && (
      doc.references.compact === queryCompact
      || (/[0-9]/.test(queryCompact) && containsWord(doc.references, queryCompact))
    )) return { matchedVariantId: '', reason: 'exact_reference', score: 29000 };
    if ([doc.model, doc.series].some((candidate) => candidate.compact && (
      candidate.compact === queryCompact
      || (/[0-9]/.test(queryCompact) && containsWord(candidate, queryCompact))
    ))) return { matchedVariantId: '', reason: 'exact_model', score: 28000 };
    if (doc.handle.compact === queryCompact) return { matchedVariantId: '', reason: 'exact_handle', score: 27000 };
    return null;
  }

  function manufacturerConflict(doc, plan, vocabulary) {
    if (!plan.manufacturer || !doc.manufacturer.normalized) return false;
    const requested = vocabulary.aliases?.[plan.manufacturer.key] || plan.manufacturer.key;
    const actual = vocabulary.aliases?.[doc.manufacturer.normalized] || doc.manufacturer.normalized;
    if (actual === requested || compact(actual) === compact(requested)) return false;
    return !hasManufacturerSignal(doc.compatibility, requested, vocabulary)
      && !hasManufacturerSignal(doc.keywords, requested, vocabulary);
  }

  function hasManufacturerSignal(fieldValue, requestedInput, vocabulary) {
    const requested = vocabulary?.aliases?.[requestedInput] || requestedInput;
    const accepted = new Set([requestedInput, requested]);
    Object.entries(vocabulary?.aliases || {}).forEach(([alias, canonical]) => {
      const resolved = vocabulary?.aliases?.[canonical] || canonical;
      if (resolved === requested) accepted.add(alias);
    });
    return [...accepted].some((candidate) => (
      containsWord(fieldValue, candidate) || exactCompact(fieldValue, candidate)
    ));
  }

  function diagnosisRank(product, doc, plan, options) {
    const terms = plan.diagnosis.terms.map(normalize).filter(Boolean);
    let best = null;
    terms.forEach((term) => {
      const match = bestTokenMatch(doc, term, false, options?._fuzzyCache);
      if (!match || match.fieldWeight < 50) return;
      const score = 1300 + match.score;
      if (!best || score > best.score) best = { score, term, match };
    });
    if (!best) return null;
    return {
      product,
      score: best.score,
      reasons: ['approved_diagnosis_map', `diagnosis:${best.term}`],
      matchedVariantId: '',
      diagnosisDisclaimer: plan.diagnosis.disclaimer !== false,
      nativeRank: Number.isFinite(product?.nativeRank) ? product.nativeRank : Number.MAX_SAFE_INTEGER,
      stableKey: `${doc.handle.normalized}|${doc.id}`,
      debug: options?.debug ? { plan, diagnosis: best } : undefined,
    };
  }

  function rank(product, query, options) {
    const vocabulary = options?.vocabulary || createVocabulary([product], options?.manualVocabulary);
    const plan = typeof query === 'object' && query?.normalizedInput !== undefined ? query : buildQueryPlan(query, vocabulary);
    if (!plan.normalizedInput || plan.normalizedInput.length < 2) return null;
    if (product?.status && product.status !== 'ACTIVE') return null;
    if (Object.prototype.hasOwnProperty.call(product || {}, 'publishedAt') && !product.publishedAt) return null;
    const doc = productDocument(product);
    if (!doc.id && !doc.handle.normalized) return null;

    const identifier = exactIdentifier(doc, compact(plan.raw));
    if (identifier) {
      return {
        product,
        score: identifier.score,
        reasons: [identifier.reason],
        matchedVariantId: identifier.matchedVariantId,
        nativeRank: Number.isFinite(product?.nativeRank) ? product.nativeRank : Number.MAX_SAFE_INTEGER,
        stableKey: `${doc.handle.normalized}|${doc.id}`,
        debug: options?.debug ? { plan } : undefined,
      };
    }

    if (doc.title.compact === plan.compact) {
      return {
        product,
        score: 20000,
        reasons: ['exact_title'],
        matchedVariantId: '',
        nativeRank: Number.isFinite(product?.nativeRank) ? product.nativeRank : Number.MAX_SAFE_INTEGER,
        stableKey: `${doc.handle.normalized}|${doc.id}`,
        debug: options?.debug ? { plan } : undefined,
      };
    }

    if (manufacturerConflict(doc, plan, vocabulary)) return null;
    if (plan.diagnosis) return diagnosisRank(product, doc, plan, options);

    const important = plan.importantTokens.filter((token) => !plan.manufacturer || token !== plan.manufacturer.key);
    const canonicalManufacturer = vocabulary.aliases?.[doc.manufacturer.normalized] || doc.manufacturer.normalized;
    const canonicalRequestedManufacturer = plan.manufacturer
      ? (vocabulary.aliases?.[plan.manufacturer.key] || plan.manufacturer.key)
      : '';
    const manufacturerExact = plan.manufacturer
      ? compact(canonicalManufacturer) === compact(canonicalRequestedManufacturer)
        || compact(canonicalManufacturer) === compact(plan.manufacturer.label)
      : false;
    if (
      plan.manufacturer
      && !manufacturerExact
      && !hasManufacturerSignal(doc.compatibility, plan.manufacturer.key, vocabulary)
      && !hasManufacturerSignal(doc.keywords, plan.manufacturer.key, vocabulary)
    ) {
      return null;
    }

    const tokenMatches = [];
    for (const token of important) {
      // Technical identifiers may use exact separator variants and safe
      // prefixes, but never typo/fuzzy matching against a different code.
      const match = bestTokenMatch(doc, token, !/\d/.test(token), options?._fuzzyCache);
      if (!match) return null;
      tokenMatches.push({ token, ...match });
    }
    if (!important.length && !manufacturerExact) return null;

    const reasons = [];
    let score = 0;
    const exactPhraseTitle = plan.compact.length >= 3 && containsCompact(doc.title, plan.normalized);
    const exactPhraseStructured = plan.compact.length >= 3 && [doc.model, doc.series, doc.productType, doc.group].some((candidate) => containsCompact(candidate, plan.normalized));
    if (doc.title.compact === plan.compact) {
      score += 7200;
      reasons.push('exact_title');
    } else if (exactPhraseTitle) {
      score += 5400;
      if (plan.technicalTokens.length) score += Math.max(0, 600 - doc.title.normalized.length * 5);
      reasons.push('exact_title_phrase');
    } else if (exactPhraseStructured) {
      score += 5000;
      reasons.push('exact_structured_phrase');
    }
    if (manufacturerExact) {
      score += 6500;
      reasons.push('exact_manufacturer');
    }
    if (plan.manufacturer && important.length && manufacturerExact) {
      score += 1800;
      reasons.push('manufacturer_plus_intent');
    }
    if (plan.correctionApplied) {
      score += 700;
      reasons.push('approved_correction');
    }
    tokenMatches.forEach((match) => {
      score += match.score;
      reasons.push(`${match.kind}:${match.token}`);
    });
    if (tokenMatches.length && tokenMatches.every((match) => match.kind === 'exact' || match.kind === 'compact')) {
      score += 1200;
      reasons.push('all_tokens_exact');
    }
    if (!score) return null;

    let matchedVariantId = '';
    if (plan.technicalTokens.length) {
      for (const token of plan.technicalTokens) {
        const queryCode = compact(token);
        const exactVariant = doc.variants.find((variant) => variant.sku.compact === queryCode);
        if (exactVariant) {
          matchedVariantId = exactVariant.id;
          score += 3000;
          reasons.push('exact_variant_sku_token');
          break;
        }
      }
    }

    return {
      product,
      score,
      reasons,
      matchedVariantId,
      nativeRank: Number.isFinite(product?.nativeRank) ? product.nativeRank : Number.MAX_SAFE_INTEGER,
      stableKey: `${doc.handle.normalized}|${doc.id}`,
      debug: options?.debug ? { plan, tokenMatches } : undefined,
    };
  }

  function finalizeSearch(deduplicated, options) {
    const nativeRanks = options?.nativeRanks && typeof options.nativeRanks === 'object' ? options.nativeRanks : null;
    const results = [...deduplicated.values()];
    if (nativeRanks) {
      results.forEach((result) => {
        const id = numericId(result.product?.id || result.product?.productId);
        const value = Number(nativeRanks[id]);
        if (Number.isFinite(value)) result.nativeRank = value;
      });
    }
    results.sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      if (left.nativeRank !== right.nativeRank) return left.nativeRank - right.nativeRank;
      return left.stableKey.localeCompare(right.stableKey, 'und', { numeric: true, sensitivity: 'base' });
    });
    const limit = Number(options?.limit ?? options?.maxResults);
    return Number.isFinite(limit) && limit >= 0 ? results.slice(0, limit) : results;
  }

  function search(products, query, options) {
    const source = Array.isArray(products) ? products : [];
    const vocabulary = options?.vocabulary || createVocabulary(source, options?.manualVocabulary);
    const plan = buildQueryPlan(query, vocabulary);
    if (!plan.normalizedInput || plan.normalizedInput.length < 2) return [];
    const deduplicated = new Map();
    const fuzzyCache = new Map();
    source.forEach((product) => {
      const result = rank(product, plan, { ...options, vocabulary, _fuzzyCache: fuzzyCache });
      if (!result) return;
      const id = numericId(product?.id || product?.productId) || scalar(product?.handle);
      const current = deduplicated.get(id);
      if (!current || result.score > current.score) deduplicated.set(id, result);
    });
    return finalizeSearch(deduplicated, options);
  }

  async function searchChunked(products, query, options) {
    const source = Array.isArray(products) ? products : [];
    const vocabulary = options?.vocabulary || createVocabulary(source, options?.manualVocabulary);
    const plan = buildQueryPlan(query, vocabulary);
    if (!plan.normalizedInput || plan.normalizedInput.length < 2) return [];
    const deduplicated = new Map();
    const fuzzyCache = new Map();
    const yieldEvery = Math.max(32, Number.parseInt(options?.yieldEvery, 10) || 160);
    let chunkStarted = performance.now();
    for (let index = 0; index < source.length; index += 1) {
      if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const product = source[index];
      const result = rank(product, plan, { ...options, vocabulary, _fuzzyCache: fuzzyCache });
      if (result) {
        const id = numericId(product?.id || product?.productId) || scalar(product?.handle);
        const current = deduplicated.get(id);
        if (!current || result.score > current.score) deduplicated.set(id, result);
      }
      if (index > 0 && (index % yieldEvery === 0 || performance.now() - chunkStarted >= 8)) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        chunkStarted = performance.now();
      }
    }
    return finalizeSearch(deduplicated, options);
  }

  function suggest(query, vocabularyInput, products, options) {
    const vocabulary = vocabularyInput || createVocabulary(products || [], options?.manualVocabulary);
    const normalizedQuery = normalize(query);
    if (normalizedQuery.length < 2) return [];
    const compactQuery = compact(normalizedQuery);
    const maximum = Math.min(MAX_SUGGESTIONS, Math.max(0, Number(options?.limit ?? options?.maxSuggestions ?? MAX_SUGGESTIONS)));
    const candidates = new Map();

    function add(label, type, score, reason) {
      const clean = scalar(label).trim().replace(/\s+/g, ' ').slice(0, MAX_QUERY_LENGTH);
      const key = compact(clean);
      if (!clean || !key || key === compactQuery && normalize(clean) === normalizedQuery) return;
      const current = candidates.get(key);
      if (!current || score > current.score) candidates.set(key, { type, term: clean, label: clean, reason, score });
    }

    const correction = vocabulary.corrections?.[normalizedQuery];
    if (correction) add(correction.label, 'query', 10000, 'correction');

    vocabulary.entries.forEach((entry) => {
      if (!entry?.label || entry.kind === 'diagnosis' && !entry.normalized.startsWith(normalizedQuery)) return;
      let score = 0;
      let reason = 'term';
      if (entry.normalized.startsWith(normalizedQuery) || entry.compact.startsWith(compactQuery)) {
        score = 7000 - Math.max(0, entry.normalized.length - normalizedQuery.length) * 4;
        reason = 'prefix';
      } else if (entry.normalized.includes(normalizedQuery) || entry.compact.includes(compactQuery)) {
        score = 4500;
        reason = 'contains';
      } else {
        const maximumDistance = fuzzyLimit(compactQuery);
        if (maximumDistance && Math.abs(entry.compact.length - compactQuery.length) <= maximumDistance) {
          const distance = editDistance(entry.compact, compactQuery, maximumDistance);
          if (distance <= maximumDistance) {
            score = 3000 - distance * 500;
            reason = 'fuzzy';
          }
        }
      }
      if (!score) return;
      const kindBonus = entry.kind === 'manufacturer' ? 500 : entry.kind === 'model' || entry.kind === 'series' ? 350 : entry.kind === 'category' ? 250 : 0;
      add(entry.label, entry.kind === 'phrase' ? 'query' : 'term', score + kindBonus + Math.min(300, entry.frequency), reason);
    });

    return [...candidates.values()]
      .sort((left, right) => right.score - left.score || left.label.localeCompare(right.label, 'und', { sensitivity: 'base' }))
      .slice(0, maximum)
      .map(({ type, term, label, reason }) => ({ type, term, label, reason }));
  }

  function createState(initial) {
    const state = initial && typeof initial === 'object' ? initial : {};
    return {
      q: scalar(state.q ?? state.query).slice(0, MAX_QUERY_LENGTH),
      correction: scalar(state.correction).slice(0, MAX_QUERY_LENGTH),
      context: state.context === 'finder' ? 'finder' : 'collection',
      collectionHandle: scalar(state.collectionHandle).slice(0, 100),
      filters: state.filters && typeof state.filters === 'object' ? { ...state.filters } : {},
      sort: scalar(state.sort || 'relevance').slice(0, 80),
      page: Math.max(1, Number.parseInt(state.page, 10) || 1),
      visible: Math.max(0, Number.parseInt(state.visible, 10) || 0),
    };
  }

  function serializeState(initial) {
    const state = createState(initial);
    const params = new URLSearchParams();
    params.set('q', state.q);
    if (state.correction) params.set('tm24_correction', state.correction);
    params.set('tm24_context', state.context);
    if (state.collectionHandle) params.set('tm24_collection', state.collectionHandle);
    if (Object.keys(state.filters).length) params.set('tm24_filters', JSON.stringify(state.filters));
    params.set('sort_by', state.sort);
    params.set('page', String(state.page));
    params.set('tm24_visible', String(state.visible));
    return params.toString();
  }

  function parseState(searchInput) {
    const params = searchInput instanceof URLSearchParams
      ? searchInput
      : new URLSearchParams(scalar(searchInput).replace(/^\?/, ''));
    let filters = {};
    try {
      const parsed = JSON.parse(params.get('tm24_filters') || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) filters = parsed;
    } catch (_) {
      filters = {};
    }
    return createState({
      q: params.get('tm24_q') ?? params.get('q') ?? '',
      correction: params.get('tm24_correction') ?? '',
      context: params.get('tm24_context') ?? 'collection',
      collectionHandle: params.get('tm24_collection') ?? '',
      filters,
      sort: params.get('sort_by') ?? 'relevance',
      page: params.get('page') ?? 1,
      visible: params.get('tm24_visible') ?? 0,
    });
  }

  return Object.freeze({
    VERSION,
    normalize,
    compact,
    buildQueryPlan,
    createVocabulary,
    rank,
    search,
    searchChunked,
    suggest,
    createState,
    serializeState,
    parseState,
  });
});